'use client'

import React from 'react'
import { RefreshCw } from 'lucide-react'
import { formatAge } from '../lib/format'
import { useAlkanes } from '../lib/store'

/// Re-render on a timer so an age label counts up on its own. Only mounted
/// while something is actually degraded, so the healthy page pays nothing.
const TICK_MS = 15_000

function useAge(since: number | null): string | null {
	const [now, setNow] = React.useState(() => Date.now())
	const active = since !== null
	React.useEffect(() => {
		if (!active) {
			return
		}
		const timer = setInterval(() => setNow(Date.now()), TICK_MS)
		return () => clearInterval(timer)
	}, [active])
	return since === null ? null : formatAge(Math.max(0, now - since))
}

/// The registry is unreachable and nothing on screen is a price. One calm
/// banner above the terminal, rather than an empty state per component that
/// each read as "no markets exist".
export default function PoolsOfflineBanner() {
	const updatedAt = useAlkanes((s) => s.updatedAt)
	const onRetry = useAlkanes((s) => s.retryPools)
	const age = useAge(updatedAt)
	return (
		<div
			data-testid="alkanes-pools-offline"
			role="status"
			aria-live="polite"
			className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 rounded-lg border border-warning/40 bg-warning/10 px-4 py-2.5"
		>
			<p className="text-xs">
				<span className="font-semibold">Market data unavailable</span>
				{/* Not `text-muted-foreground`: over the warning tint that measures
				    4.17:1 in light theme, under the 4.5:1 floor. Weight carries the
				    de-emphasis instead of colour. */}
				<span className="text-foreground/80">
					{' — retrying automatically. '}
					{age
						? `Showing prices from ${age} ago.`
						: 'Prices, markets and quotes cannot be shown until it returns.'}
				</span>
			</p>
			<button
				type="button"
				data-testid="alkanes-pools-retry"
				onClick={onRetry}
				className="alkanes-touch inline-flex shrink-0 items-center gap-1.5 rounded-md border border-border bg-card px-2.5 py-1 text-xs font-medium transition-colors hover:border-primary/50 hover:text-primary"
			>
				<RefreshCw className="size-3" aria-hidden="true" />
				Retry
			</button>
		</div>
	)
}

/// The registry blinked but we still hold real numbers. The prices are true,
/// just old — which is a chip, not an alarm.
export function StalePricesChip() {
	const updatedAt = useAlkanes((s) => s.updatedAt)
	const age = useAge(updatedAt)
	return (
		// Deliberately NOT a live region: the age ticks, and re-announcing
		// "prices from 3m ago" every minute is noise. Stale-but-real numbers are
		// a fact to read in place, not an interruption — unlike the offline
		// banner above, which is.
		<span
			data-testid="alkanes-pools-stale"
			title="The pool registry is not responding — these are the last prices that arrived."
			className="rounded bg-warning/15 px-1.5 py-0.5 text-xxs font-medium tabular-nums text-muted-foreground"
		>
			{age ? `prices from ${age} ago` : 'prices may be stale'}
		</span>
	)
}
